import { Controller } from "@hotwired/stimulus"

// Programs section for rank group: toggles panel visibility, manages program_ids via hidden fields.
// Add/remove programs without form submit; switching to All Programs clears selections.
export default class extends Controller {
  static targets = ["programsPanel", "programIdsContainer", "selectedList", "emptyState", "searchInput", "searchResults"]
  static values = {
    searchUrl: String,
    paramName: { type: String, default: "rank_group[program_ids][]" },
  }

  connect() {
    this.debounceTimer = null
    this.cache = new Map()
    this.toggle()
  }

  change() {
    const scope = this.element.querySelector(`input[name="rank_group[program_scope]"]:checked`)?.value
    if (scope === "all_programs") {
      this.clearProgramIds()
    }
    this.toggle()
  }

  toggle() {
    const checked = this.element.querySelector(`input[value="selected_programs"]`)?.checked
    if (this.hasProgramsPanelTarget) {
      this.programsPanelTarget.classList.toggle("hidden", !checked)
    }
  }

  clearProgramIds() {
    if (!this.hasProgramIdsContainerTarget) return
    this.programIdsContainerTarget.innerHTML = ""
    if (this.hasSelectedListTarget) {
      this.selectedListTarget.innerHTML = ""
      this.selectedListTarget.classList.add("hidden")
    }
    if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.remove("hidden")
    if (this.hasSearchInputTarget) this.searchInputTarget.value = ""
  }

  async search() {
    const q = (this.searchInputTarget?.value || "").trim()
    const url = new URL(this.searchUrlValue, window.location.origin)
    url.searchParams.set("q", q)
    url.searchParams.set("limit", "12")
    const excludeIds = this.getSelectedIds()
    if (excludeIds.length > 0) {
      excludeIds.forEach((id) => url.searchParams.append("exclude_ids[]", id))
    }

    try {
      const res = await fetch(url.toString(), { headers: { Accept: "application/json" } })
      const data = await res.json()
      const items = Array.isArray(data) ? data : []
      this.renderResults(items)
    } catch {
      this.renderResults([])
    }
  }

  renderResults(items) {
    if (!this.hasSearchResultsTarget) return
    const html =
      items.length > 0
        ? `<ul class="py-1">${items
            .map(
              (p) => `
        <li role="option" data-id="${p.id}" data-label="${escapeHtml(p.label || p.name || "")}"
            class="px-3 py-2 text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
            data-action="click->rank-group-programs#selectProgram">
          ${escapeHtml(p.label || p.name || String(p.id))}
        </li>`
            )
            .join("")}</ul>`
        : ""
    this.searchResultsTarget.innerHTML = html
    this.searchResultsTarget.classList.toggle("hidden", items.length === 0)
    this.searchResultsTarget.classList.toggle("block", items.length > 0)
  }

  selectProgram(e) {
    const el = e.currentTarget
    const id = el.getAttribute("data-id")
    const label = el.getAttribute("data-label")
    if (!id || this.getSelectedIds().includes(id)) return

    const input = document.createElement("input")
    input.type = "hidden"
    input.name = this.paramNameValue
    input.value = id
    input.dataset.programId = id
    this.programIdsContainerTarget.appendChild(input)

    const li = document.createElement("li")
    li.className = "flex items-center justify-between py-4 first:pt-0 last:pb-0 border-b border-gray-200 dark:border-gray-700 last:border-0"
    li.dataset.programId = id
    li.innerHTML = `
      <span class="text-gray-900 dark:text-white">${escapeHtml(label)}</span>
      <button type="button" class="text-sm text-rose-600 hover:underline" data-action="click->rank-group-programs#removeProgram" data-program-id="${id}">Remove</button>
    `
    this.selectedListTarget.appendChild(li)
    this.selectedListTarget.classList.remove("hidden")
    if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.add("hidden")

    this.searchInputTarget.value = ""
    this.searchInputTarget.dispatchEvent(new Event("input", { bubbles: true }))
  }

  removeProgram(e) {
    const id = e.currentTarget.dataset.programId
    this.programIdsContainerTarget.querySelector(`input[data-program-id="${id}"]`)?.remove()
    this.selectedListTarget.querySelector(`li[data-program-id="${id}"]`)?.remove()
    if (this.getSelectedIds().length === 0) {
      this.selectedListTarget.classList.add("hidden")
      if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.remove("hidden")
    }
  }

  getSelectedIds() {
    if (!this.hasProgramIdsContainerTarget) return []
    return Array.from(this.programIdsContainerTarget.querySelectorAll("input[data-program-id]")).map(
      (i) => i.dataset.programId
    )
  }

  input() {
    clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.search(), 200)
  }

  focus() {
    const q = (this.searchInputTarget?.value || "").trim()
    if (q.length === 0) this.search()
  }

  blur() {
    setTimeout(() => {
      if (this.hasSearchResultsTarget) {
        this.searchResultsTarget.classList.add("hidden")
        this.searchResultsTarget.classList.remove("block")
      }
    }, 150)
  }
}

function escapeHtml(s) {
  const div = document.createElement("div")
  div.textContent = s || ""
  return div.innerHTML
}
